import { applyTransformToCanvas } from './floorplanTransform';
import { processWatermark, drawWatermark, type WatermarkOptions } from '@/services/imageProcessing';
import type { FloorplanTransform, AspectRatio, LogoPosition, WatermarkPosition, FloorplanArrow } from '@/types';

export interface CompositeWatermarkInput {
  url: string;
  options: WatermarkOptions;
}

export interface CompositeLogoInput {
  url: string;
  position: LogoPosition;
  /** Logo width as fraction of frame width. Defaults to 0.15. */
  scale?: number;
}

interface RenderCompositeArgs {
  imageUrl: string;
  transform: FloorplanTransform;
  frameRatio: AspectRatio;
  overlayUrl?: string | null;
  watermark?: CompositeWatermarkInput | null;
  logo?: CompositeLogoInput | null;
  arrow?: FloorplanArrow | null;
  outputLongEdge?: number; // default 2048
}

const loadImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(new Error(`Image load failed: ${url} — ${String(e)}`));
    img.src = url;
  });

/**
 * Layer 2: draws an overlay image stretched to the full frame.
 * Exposed for live-preview callers that need to replicate the composite's
 * layer pipeline on a work-in-progress canvas.
 */
export async function drawOverlayLayer(
  ctx: CanvasRenderingContext2D,
  overlayUrl: string,
  frame: { width: number; height: number }
): Promise<void> {
  const overlay = await loadImage(overlayUrl);
  ctx.drawImage(overlay, 0, 0, frame.width, frame.height);
}

/**
 * Layer 3: draws a processed watermark onto the composite canvas.
 * Reuses `processWatermark` + `drawWatermark` from services/imageProcessing
 * so watermark appearance/positioning stays in sync with the rest of the app.
 */
export async function drawWatermarkLayer(
  ctx: CanvasRenderingContext2D,
  watermark: CompositeWatermarkInput,
  frame: { width: number; height: number }
): Promise<void> {
  const processedCanvas = await processWatermark(
    watermark.url,
    frame.width,
    frame.height,
    watermark.options
  );
  drawWatermark(ctx, processedCanvas, frame.width, frame.height, watermark.options.position);
}

/**
 * Layer 4: draws a logo image at one of 4 corners.
 * Logo width defaults to 15 % of the frame width, aspect-ratio preserved.
 * Margin is 3 % of frame width.
 */
export async function drawLogoLayer(
  ctx: CanvasRenderingContext2D,
  logo: CompositeLogoInput,
  frame: { width: number; height: number }
): Promise<void> {
  const img = await loadImage(logo.url);

  const scale = logo.scale ?? 0.15;
  const targetWidth = Math.round(frame.width * scale);
  const aspect =
    img.naturalWidth > 0 ? img.naturalHeight / img.naturalWidth : 1;
  const targetHeight = Math.round(targetWidth * aspect);
  const margin = Math.round(frame.width * 0.03);

  const anchors: Record<LogoPosition, { x: number; y: number }> = {
    'top-left': { x: margin, y: margin },
    'top-right': { x: frame.width - targetWidth - margin, y: margin },
    'bottom-left': { x: margin, y: frame.height - targetHeight - margin },
    'bottom-right': {
      x: frame.width - targetWidth - margin,
      y: frame.height - targetHeight - margin,
    },
  };

  const { x, y } = anchors[logo.position];
  ctx.drawImage(img, x, y, targetWidth, targetHeight);
}

/**
 * Layer 5: draws the entrance-triangle overlay (Eingangsdreieck).
 * Drawn in canvas 2D with the same triangle geometry as the on-screen SVG
 * so preview and export match.
 */
export async function drawArrowLayer(
  ctx: CanvasRenderingContext2D,
  arrow: FloorplanArrow | null,
  frame: { width: number; height: number }
): Promise<void> {
  if (!arrow) return;

  const sizePx = arrow.size * frame.width;
  const cx = arrow.x * frame.width;
  const cy = arrow.y * frame.height;

  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate((arrow.rotation * Math.PI) / 180);

  const s = sizePx / 24;
  ctx.beginPath();
  ctx.moveTo((12 - 12) * s, (2 - 12) * s);
  ctx.lineTo((22 - 12) * s, (22 - 12) * s);
  ctx.lineTo((2 - 12) * s, (22 - 12) * s);
  ctx.closePath();
  ctx.fillStyle = '#111';
  ctx.fill();
  ctx.strokeStyle = '#fff';
  ctx.lineWidth = Math.max(1, s);
  ctx.stroke();

  ctx.restore();
}

/**
 * Render the final floorplan composite.
 * Z-order (bottom → top):
 *   1. floorplan (with transform)
 *   2. overlay
 *   3. watermark
 *   4. logo
 *   5. entrance triangle (Eingangsdreieck)
 */
export async function renderComposite({
  imageUrl,
  transform,
  frameRatio,
  overlayUrl,
  watermark,
  logo,
  arrow,
  outputLongEdge = 2048,
}: RenderCompositeArgs): Promise<Blob> {
  const ratio = frameRatio === '1:1' ? 1 : 4 / 3;
  const width = outputLongEdge;
  const height = Math.round(outputLongEdge / ratio);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context not available');

  // Layer 1: floorplan with transform
  const image = await loadImage(imageUrl);
  applyTransformToCanvas(ctx, image, transform, { width, height });

  // Layer 2: overlay
  if (overlayUrl) {
    const overlay = await loadImage(overlayUrl);
    ctx.drawImage(overlay, 0, 0, width, height);
  }

  // Layer 3: watermark
  if (watermark) {
    await drawWatermarkLayer(ctx, watermark, { width, height });
  }

  // Layer 4: logo
  if (logo) {
    await drawLogoLayer(ctx, logo, { width, height });
  }

  // Layer 5: entrance triangle (Eingangsdreieck)
  await drawArrowLayer(ctx, arrow ?? null, { width, height });

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('canvas.toBlob returned null'))),
      'image/png'
    );
  });
}

export type { WatermarkPosition, LogoPosition };
